import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import productBundleService, { ProductBundle } from '../../services/productBundleService';
import { useAuth } from '../../context/AuthContext';
import { Permissions } from '../../constants/Permissions';
import { getServerErrorMessage } from '../../utils/errorUtils';
import '../../styles/ImsTheme.css';
import './ProductBundleListPage.css';
import { CirclePlus, RefreshCw, SquarePen, Trash2, Package, CircleCheck, Layers } from 'lucide-react';

const ProductBundleListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();

  const [bundles, setBundles] = useState<ProductBundle[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(
    (location.state as { message?: string } | null)?.message ?? null
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      setBundles(await productBundleService.getAll());
    } catch (err) {
      setError(getServerErrorMessage(err) || t('productBundle.failedToLoad'));
    }
  }, [t]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  // Clear the one-shot banner from navigation state so a browser refresh does not replay it.
  useEffect(() => {
    if ((location.state as { message?: string } | null)?.message) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const handleDelete = async (bundle: ProductBundle) => {
    if (!window.confirm(t('productBundle.confirmDelete', { name: bundle.name }))) return;
    try {
      await productBundleService.delete(bundle.id);
      setSuccessMessage(t('productBundle.deleteSuccess'));
      await load();
    } catch (err) {
      setError(getServerErrorMessage(err) || t('productBundle.failedToDelete'));
    }
  };

  const formatPrice = (value: number) =>
    new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 2 }).format(value);

  const columns: GridColumn[] = [
    {
      key: 'code',
      label: t('productBundle.code'),
      sortable: true,
      width: '140px',
      render: (value) => <strong>{value}</strong>,
    },
    {
      key: 'name',
      label: t('productBundle.name'),
      sortable: true,
      width: '220px',
    },
    {
      key: 'description',
      label: t('productBundle.description'),
      render: (value) => <span className="description-text">{value || '—'}</span>,
    },
    {
      key: 'price',
      label: t('productBundle.price'),
      sortable: true,
      width: '120px',
      render: (value) => formatPrice(value as number),
    },
    {
      key: 'items',
      label: t('productBundle.itemCount'),
      width: '110px',
      render: (_value, row) => (row as ProductBundle).items?.length ?? 0,
    },
    {
      key: 'sortOrder',
      label: t('productBundle.sortOrder'),
      sortable: true,
      width: '110px',
    },
    {
      key: 'isActive',
      label: t('common.status'),
      width: '120px',
      render: (value) =>
        value ? (
          <span className="ims-badge ims-badge-approved">{t('common.active')}</span>
        ) : (
          <span className="ims-badge ims-badge-closed">{t('common.inactive')}</span>
        ),
    },
  ];

  const actions: GridAction[] = [
    ...(hasPermission(Permissions.ProductBundles.Edit) ? [{
      icon: <SquarePen size={16} strokeWidth={1.75} />,
      onClick: (item: any) => navigate(`/sales/product-bundles/${item.id}/edit`),
      className: 'grid-action-btn-edit',
      title: t('common.edit'),
    }] : []),
    ...(hasPermission(Permissions.ProductBundles.Delete) ? [{
      icon: <Trash2 size={16} strokeWidth={1.75} />,
      onClick: (item: any) => handleDelete(item as ProductBundle),
      className: 'grid-action-btn-delete',
      title: t('common.delete'),
    }] : []),
  ];

  const activeCount = bundles.filter((b) => b.isActive).length;
  const totalItems = bundles.reduce((sum, bundle) => sum + (bundle.items?.length ?? 0), 0);

  return (
    <Layout>
      <div className="container product-bundle-page">
        <div className="header">
          <h1 className="title">{t('productBundle.title')}</h1>
          <div className="header-actions">
            {hasPermission(Permissions.ProductBundles.Create) && (
              <button
                className="btn-new"
                onClick={() => navigate('/sales/product-bundles/create')}
                title={t('productBundle.newBundle')}
              >
                <CirclePlus />
                {t('productBundle.newBundle')}
              </button>
            )}
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('common.refresh')}
            >
              <RefreshCw size={18} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('common.refresh')}
            </button>
          </div>
        </div>

        <div className="ims-stats-row">
          <div className="ims-stat-card">
            <div className="ims-stat-icon bundle-icon-total"><Package size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{bundles.length}</span>
              <span className="ims-stat-label">{t('productBundle.totalBundles')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon bundle-icon-active"><CircleCheck size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{activeCount}</span>
              <span className="ims-stat-label">{t('common.active')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon bundle-icon-items"><Layers size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{totalItems}</span>
              <span className="ims-stat-label">{t('productBundle.totalItems')}</span>
            </div>
          </div>
        </div>

        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show">
            {successMessage}
            <button
              type="button"
              className="btn-close"
              onClick={() => setSuccessMessage(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <DataGrid
          data={bundles}
          columns={columns}
          actions={actions}
          loading={loading}
          pageSize={10}
          searchPlaceholder={t('common.search')}
          emptyMessage={t('productBundle.noBundlesFound')}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </div>
    </Layout>
  );
};

export default ProductBundleListPage;
